import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { type ParsedApiError } from '@loctopus/shared';
import { ErrorSummary } from '@loctopus/ui';
import { api } from '../../lib/api';
import { toParsedError } from '../../lib/errorDisplay';
import {
  exportSeatMapToJson,
  parseSeatMapImport,
  SeatMapImportError,
  type SeatMapImportDocument,
  type SeatMapImportSection,
} from '../../lib/seatmap-shape';
import {
  Badge,
  Button,
  Card,
  ErrorBanner,
  FormField,
  Input,
  Modal,
  ModuleNotIncluded,
  PageHeader,
  Spinner,
  Textarea,
  Toggle,
} from '../../components/ui';
import { RequirePermission } from '../../lib/auth';
import { useClientPackage } from '../../lib/clientPackage';
import type { SeatMap } from '../../lib/types';

const SECTION_COLORS = ['#2B3A55', '#7B5EA7', '#C97B4A', '#3C7A5F', '#B23A52', '#4A6FA5'];
const CANVAS_WIDTH = 900;
const CANVAS_HEIGHT = 560;
const SEAT_RADIUS = 9;

function emptyDoc(name: string): SeatMapImportDocument {
  return { version: 1, name, sections: [] };
}

function nextSeatPosition(section: SeatMapImportSection): { x: number; y: number } {
  const count = section.seats.length;
  const col = count % 10;
  const row = Math.floor(count / 10);
  return { x: 60 + col * 32, y: 60 + row * 32 };
}

/**
 * Rough first pass (flagged per task brief): drag repositioning is per-seat only (no
 * multi-select/marquee, no snap-to-grid beyond the initial auto-layout, no section-level drag of
 * the whole section as a group, no collision detection between sections). Good enough to lay out
 * and tweak a seat map; polishing is left for a later pass.
 */
export function SeatMapEditorPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const svgRef = useRef<SVGSVGElement>(null);

  const [existingMapId, setExistingMapId] = useState<string | null>(null);
  const [doc, setDoc] = useState<SeatMapImportDocument>(emptyDoc('Main seat map'));
  const [importOpen, setImportOpen] = useState(false);
  const [saveError, setSaveError] = useState<ParsedApiError | null>(null);
  const [drag, setDrag] = useState<{ sectionIdx: number; seatIdx: number } | null>(null);
  const { hasModule, isLoading: pkgLoading } = useClientPackage();

  const seatMapsQuery = useQuery({
    queryKey: ['seat-maps', eventId],
    queryFn: () => api.seatMaps.list(eventId!),
    enabled: !!eventId,
  });

  useEffect(() => {
    const first: SeatMap | undefined = seatMapsQuery.data?.data[0];
    if (first) {
      setExistingMapId(first.id);
      const layout = first.layout as unknown;
      try {
        setDoc(parseSeatMapImport(JSON.stringify(layout)));
      } catch {
        setDoc({ version: 1, name: first.name, sections: [] });
      }
    }
  }, [seatMapsQuery.data]);

  // REAL BUG FOUND AND FIXED: this used to send the whole internal editor doc as an opaque
  // `layout` blob - the real import route (apps/api/src/routes/management/catalog.ts,
  // importSeatMapSchema) requires a top-level `sections` array with `rowLabel`/`seatNumber`
  // (this doc's `row`/`number`, renamed - see lib/seatmap-shape.ts's own "FLAG FOR THE
  // TICKETING-CORE AGENT" comment, which already predicted exactly this mismatch and was never
  // reconciled). Every save has always 400'd with "sections: Required". `layout` is still sent
  // too (the backend stores it as an opaque blob for the editor's own future use, per its doc
  // comment) so re-opening this editor can still recover the exact canvas doc, not just the
  // normalized sections/seats the backend understands.
  function toImportPayload() {
    return {
      name: doc.name,
      layout: doc as unknown as Record<string, unknown>,
      sections: doc.sections.map((s) => ({
        name: s.name,
        priceZone: s.priceZone,
        isAccessible: s.isAccessible,
        seats: s.seats.map((seat) => ({
          rowLabel: seat.row,
          seatNumber: seat.number,
          ...(seat.x != null ? { x: seat.x } : {}),
          ...(seat.y != null ? { y: seat.y } : {}),
        })),
      })),
    };
  }

  const saveMutation = useMutation({
    mutationFn: async (): Promise<{ seatMapId: string }> => {
      if (existingMapId) {
        // REAL GAP FOUND AND FIXED: `PATCH /dashboard/seat-maps/:id` now exists server-side
        // (apps/client-api/src/routes/management/catalog.ts) - edits this seat map's own
        // name/layout in place instead of always creating a brand new one on every save.
        const saved = await api.seatMaps.update(existingMapId, {
          name: doc.name,
          layout: doc as unknown as Record<string, unknown>,
        });
        return { seatMapId: saved.id };
      }
      return api.seatMaps.create(eventId!, toImportPayload());
    },
    onSuccess: (saved) => {
      setExistingMapId(saved.seatMapId);
      setSaveError(null);
      queryClient.invalidateQueries({ queryKey: ['seat-maps', eventId] });
    },
    onError: (err) => setSaveError(toParsedError(err)),
  });

  if (!eventId) return null;

  function addSection() {
    setDoc((d) => ({
      ...d,
      sections: [
        ...d.sections,
        {
          name: `Section ${d.sections.length + 1}`,
          priceZone: 'standard',
          isAccessible: false,
          seats: [],
        },
      ],
    }));
  }

  function removeSection(idx: number) {
    setDoc((d) => ({ ...d, sections: d.sections.filter((_, i) => i !== idx) }));
  }

  function updateSection(idx: number, patch: Partial<SeatMapImportSection>) {
    setDoc((d) => ({
      ...d,
      sections: d.sections.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    }));
  }

  function addSeat(sectionIdx: number) {
    setDoc((d) => {
      const section = d.sections[sectionIdx];
      if (!section) return d;
      const rowLabel = String.fromCharCode(65 + Math.floor(section.seats.length / 10));
      const pos = nextSeatPosition(section);
      const seat = {
        row: rowLabel,
        number: String((section.seats.length % 10) + 1),
        x: pos.x,
        y: pos.y,
        isAccessible: false,
        status: 'available' as const,
      };
      const sections = d.sections.map((s, i) =>
        i === sectionIdx ? { ...s, seats: [...s.seats, seat] } : s,
      );
      return { ...d, sections };
    });
  }

  function removeSeat(sectionIdx: number, seatIdx: number) {
    setDoc((d) => ({
      ...d,
      sections: d.sections.map((s, i) =>
        i === sectionIdx ? { ...s, seats: s.seats.filter((_, j) => j !== seatIdx) } : s,
      ),
    }));
  }

  function toggleSeatAccessible(sectionIdx: number, seatIdx: number) {
    setDoc((d) => ({
      ...d,
      sections: d.sections.map((s, i) =>
        i === sectionIdx
          ? {
              ...s,
              seats: s.seats.map((seat, j) =>
                j === seatIdx ? { ...seat, isAccessible: !seat.isAccessible } : seat,
              ),
            }
          : s,
      ),
    }));
  }

  function onPointerMove(e: ReactPointerEvent<SVGSVGElement>) {
    if (!drag || !svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * CANVAS_WIDTH);
    const y = Math.round(((e.clientY - rect.top) / rect.height) * CANVAS_HEIGHT);
    setDoc((d) => ({
      ...d,
      sections: d.sections.map((s, i) =>
        i === drag.sectionIdx
          ? {
              ...s,
              seats: s.seats.map((seat, j) => (j === drag.seatIdx ? { ...seat, x, y } : seat)),
            }
          : s,
      ),
    }));
  }

  // docs/ARCHITECTURE_V2.md §3.4/§12 agent 7: courtesy-only gate, see PromoCodesPage.tsx's
  // identical comment. Module key is `reservedSeating` per §6's schema.
  if (!pkgLoading && !hasModule('reservedSeating')) {
    return (
      <div>
        <PageHeader
          title="Seat map editor"
          actions={
            <Button variant="ghost" onClick={() => navigate(`/events/${eventId}`)}>
              Back to event
            </Button>
          }
        />
        <ModuleNotIncluded feature="Reserved seating" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={`Seat map editor - ${doc.name}`}
        description={
          existingMapId
            ? `Editing the saved seat map for this event (id: ${existingMapId}). Price zones are referenced by the ticket type editor.`
            : 'No seat map saved for this event yet - build one below, then save to create it. Price zones are referenced by the ticket type editor.'
        }
        actions={
          <>
            <Button variant="secondary" onClick={() => setImportOpen(true)}>
              Import JSON
            </Button>
            <Button variant="ghost" onClick={() => exportCurrent(doc)}>
              Export JSON
            </Button>
            <RequirePermission permission="seat_maps.manage">
              <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
                {saveMutation.isPending
                  ? 'Saving...'
                  : existingMapId
                    ? 'Save changes'
                    : 'Create seat map'}
              </Button>
            </RequirePermission>
            <Button variant="ghost" onClick={() => navigate(`/events/${eventId}`)}>
              Back to event
            </Button>
          </>
        }
      />
      {saveError && <ErrorSummary error={saveError} className="mb-3" />}
      {seatMapsQuery.isLoading ? (
        <Spinner />
      ) : (
        <div className="grid grid-cols-[280px_1fr] gap-6">
          <Card title="Sections">
            <FormField label="Seat map name">
              <Input
                value={doc.name}
                onChange={(e) => setDoc((d) => ({ ...d, name: e.target.value }))}
              />
            </FormField>
            <RequirePermission permission="seat_maps.manage">
              <Button size="sm" variant="secondary" onClick={addSection} className="mb-3">
                Add section
              </Button>
            </RequirePermission>
            <div className="space-y-3">
              {doc.sections.map((section, idx) => (
                <div
                  key={idx}
                  className="border border-slate-200 rounded-brand p-3"
                  style={{
                    borderLeftColor: SECTION_COLORS[idx % SECTION_COLORS.length],
                    borderLeftWidth: 4,
                  }}
                >
                  <Input
                    value={section.name}
                    onChange={(e) => updateSection(idx, { name: e.target.value })}
                    className="mb-2"
                  />
                  <FormField label="Price zone">
                    <Input
                      value={section.priceZone}
                      onChange={(e) => updateSection(idx, { priceZone: e.target.value })}
                    />
                  </FormField>
                  <Toggle
                    checked={section.isAccessible}
                    onChange={(checked) => updateSection(idx, { isAccessible: checked })}
                    label="Accessible section"
                  />
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-slate-500">{section.seats.length} seat(s)</span>
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => addSeat(idx)}>
                        + Seat
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => removeSection(idx)}>
                        Remove
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              {doc.sections.length === 0 && (
                <p className="text-sm text-slate-500">
                  No sections yet - add one to start placing seats, or import a JSON seat map.
                </p>
              )}
            </div>
          </Card>
          <Card title="Canvas">
            <p className="text-xs text-slate-500 mb-2">
              Drag a seat to reposition it. Click a seat to toggle accessible.
            </p>
            <svg
              ref={svgRef}
              viewBox={`0 0 ${CANVAS_WIDTH} ${CANVAS_HEIGHT}`}
              className="w-full border border-slate-200 rounded-brand bg-slate-50 touch-none"
              onPointerMove={onPointerMove}
              onPointerUp={() => setDrag(null)}
              onPointerLeave={() => setDrag(null)}
            >
              {doc.sections.map((section, sIdx) =>
                section.seats.map((seat, seatIdx) => (
                  <g
                    key={`${sIdx}-${seatIdx}`}
                    transform={`translate(${seat.x ?? 0}, ${seat.y ?? 0})`}
                    onPointerDown={() => setDrag({ sectionIdx: sIdx, seatIdx })}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSeatAccessible(sIdx, seatIdx);
                    }}
                    className="cursor-move"
                  >
                    <circle
                      r={SEAT_RADIUS}
                      fill={SECTION_COLORS[sIdx % SECTION_COLORS.length]}
                      stroke={seat.isAccessible ? '#1F8A5F' : 'none'}
                      strokeWidth={seat.isAccessible ? 3 : 0}
                      opacity={seat.status === 'disabled' ? 0.3 : 1}
                    >
                      <title>{`${section.name} ${seat.row}${seat.number}${seat.isAccessible ? ' (accessible)' : ''}`}</title>
                    </circle>
                    <text y={3} textAnchor="middle" fontSize={7} fill="white">
                      {seat.row}
                      {seat.number}
                    </text>
                    <text y={SEAT_RADIUS + 10} textAnchor="middle" fontSize={8} fill="#64748B">
                      <tspan
                        onClick={(e) => {
                          e.stopPropagation();
                          removeSeat(sIdx, seatIdx);
                        }}
                        className="cursor-pointer"
                      >
                        x
                      </tspan>
                    </text>
                  </g>
                )),
              )}
            </svg>
            <div className="flex gap-3 mt-3 text-xs text-slate-500">
              {doc.sections.map((s, idx) => (
                <span key={idx} className="inline-flex items-center gap-1">
                  <span
                    className="h-2.5 w-2.5 rounded-full inline-block"
                    style={{ background: SECTION_COLORS[idx % SECTION_COLORS.length] }}
                  />
                  {s.name} ({s.priceZone})
                </span>
              ))}
            </div>
          </Card>
        </div>
      )}
      <ImportSeatMapModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={(imported) => setDoc(imported)}
      />
    </div>
  );
}

function exportCurrent(doc: SeatMapImportDocument) {
  const blob = new Blob([exportSeatMapToJson(doc)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${doc.name.replace(/\s+/g, '-').toLowerCase()}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

function ImportSeatMapModal({
  open,
  onClose,
  onImported,
}: {
  open: boolean;
  onClose: () => void;
  onImported: (doc: SeatMapImportDocument) => void;
}) {
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);

  function handleImport() {
    try {
      const parsed = parseSeatMapImport(text);
      onImported(parsed);
      setError(null);
      setText('');
      onClose();
    } catch (e) {
      setError(e instanceof SeatMapImportError ? e.message : 'Could not parse that JSON.');
    }
  }

  function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    file
      .text()
      .then(setText)
      .catch(() => setError('Could not read that file.'));
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      wide
      title="Import seat map JSON"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleImport} disabled={!text.trim()}>
            Import
          </Button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      <p className="text-xs text-slate-500 mb-3">
        Paste or upload a seat map JSON document (see <Badge tone="info">version: 1</Badge> shape in
        apps/dashboard/src/lib/seatmap-shape.ts). This has not yet been confirmed against the
        Ticketing-core agent's real importer - flagged in this build's report.
      </p>
      <FormField label="Upload a .json file">
        <input type="file" accept="application/json" onChange={handleFile} className="text-sm" />
      </FormField>
      <FormField label="Or paste JSON">
        <Textarea
          rows={10}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder='{"version":1,"name":"Main Hall","sections":[...]}'
        />
      </FormField>
    </Modal>
  );
}
